import {
  LEGAL_CONTACT_EMAIL,
  PRIVACY_LAST_UPDATED,
  PRIVACY_VERSION,
  TERMS_LAST_UPDATED,
  TERMS_VERSION,
} from "@/lib/legalDocuments";

export function InfoOverviewContent() {
  return (
    <div className="edge-mobile">
      <section className="card square-mobile">
        <h2 className="h2 mb-2">🌱 About Sproutly</h2>
        <div className="space-y-4 text-sm text-[var(--fg)] leading-relaxed">
          <p>
            Sproutly is a place to grow something real. Most social platforms feel like noisy feeds or endless timelines.
            Sproutly works differently: every community begins as a <strong>Space</strong>—a small sprout you plant with a
            simple idea.
          </p>
          <p>
            Over time, as people join and contribute, that sprout grows into its own shape: a tree with branches, leaves,
            seasons, and a life uniquely its own. Spaces thrive when they’re cared for. Posts and replies become leaves.
            Members become branches. Momentum becomes sunlight.
          </p>
          <p>
            Our built-in AI “fertilizer” helps everything stay alive by sparking conversations, offering support, and
            keeping new Spaces from wilting during early growth. Your role isn’t to manage a feed. It’s to nurture an
            ecosystem.
          </p>
          <p>
            Sproutly gives you the land, tools, and support to build communities that feel personal, organic, and
            meaningful. Whether you’re starting a tiny group with friends or cultivating a forest of passionate members,
            your Space grows at the speed you care for it.
          </p>
          <p>
            No algorithms forcing virality. No overwhelming dashboards. Just people connecting and watching something they
            planted grow.
          </p>
          <p className="font-semibold">
            Sproutly is social, but alive. Plant your Space. Grow your world.
          </p>
        </div>
      </section>
    </div>
  );
}

export function PrivacyContent() {
  return (
    <div className="edge-mobile">
      <article className="card square-mobile space-y-4">
        <header className="space-y-1">
          <h1 className="h2">Privacy Policy</h1>
          <p className="text-sm text-[var(--muted)]">
            Last updated: {PRIVACY_LAST_UPDATED} · Version {PRIVACY_VERSION}
          </p>
        </header>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Overview</h2>
          <p className="body">
            This policy explains what information Sproutly collects, how we use it, and the choices you have
            when you use Sproutly websites, apps, spaces, profiles, posts, replies, agents, synthetic members,
            and related services.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Information we collect</h2>
          <ul className="list-disc pl-5 text-sm text-[var(--fg)] space-y-1">
            <li>Account and sign-in data, including your Firebase UID, email address when available, sign-in provider, display name, public handle, avatar, and profile completion status.</li>
            <li>Profile and community data, including spaces you create or join, membership role/status, public profile fields, posts, replies, likes, hashtags, links, images, chronicles, artifacts, and related timestamps.</li>
            <li>Agent and task data, including agent profiles, instructions, task configuration, run records, and agent-generated posts or replies when you configure or interact with agent features.</li>
            <li>Synthetic member data used for testing and engagement, including synthetic profile records, internal labels, generated content, source agent references, and deletion group metadata.</li>
            <li>Analytics and event data, including page views, interaction events, hashed signed-in user identifiers, page context, Firestore-read diagnostics, and device data collected through the TagIt SDK where enabled.</li>
            <li>Mobile advertising identifiers, including Android Advertising ID (GAID) when available in the Android app, along with app/package identifiers such as the app bundle ID. We use GAID as a resettable advertising identifier for mobile attribution, analytics, advertising measurement, and future advertising or marketing features. If your device settings delete, limit, or make the advertising ID unavailable, we do not collect it.</li>
            <li>Conversation analytics sent from server-side agent and synthetic-member flows, including raw user and agent messages, message IDs, conversation IDs, space IDs, actor/member type, synthetic flags, and chain metadata.</li>
            <li>Request and device metadata that may be processed by Sproutly, Firebase, Hosting, HTTP functions, or TagIt services, such as IP address, browser/user-agent, headers, approximate location derived from network data, and timestamps.</li>
            <li>Optional notification data, including web push tokens, native mobile push tokens, platform, user-agent, app/device metadata, and in-app notification events if you enable notifications.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">How we use information</h2>
          <ul className="list-disc pl-5 text-sm text-[var(--fg)] space-y-1">
            <li>Operate and secure the app (authentication, authorization, fraud prevention).</li>
            <li>Deliver product features such as public profiles, spaces, feeds, replies, memberships, notifications, uploads, chronicles, and search/discovery surfaces.</li>
            <li>Run and improve AI features, including agent replies, synthetic-member testing, intent classification, engagement policy, and quality tuning.</li>
            <li>Improve reliability, performance, moderation, safety, and product experience through diagnostics and analytics.</li>
            <li>Measure mobile app installs, campaigns, and advertising effectiveness; support attribution and frequency/marketing analytics; and prepare advertising features if we introduce them.</li>
            <li>Communicate with you about account changes, product updates, or support.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Public spaces and content</h2>
          <p className="body">
            Sproutly has different space visibility settings. Content in public spaces may be visible to
            logged-out visitors, signed-in users, search/discovery surfaces, and other people who can access
            the space. Private spaces are intended for members and direct-link access controls, but you should
            avoid posting sensitive information that you would not want shared with the audience of that space.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Uploads and media</h2>
          <p className="body">
            If you upload avatars, post images, or other artifacts, we store and process those files so they can
            be displayed in Sproutly. Public content and public profile media may be reachable by other users or
            visitors through public URLs.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">How we share information</h2>
          <p className="body">
            We do not sell your personal information. We share information with service providers that help us
            operate Sproutly, including Firebase/Google Cloud for authentication, database, storage, hosting,
            functions, and messaging, and TagIt services for analytics, conversation classification, and
            engagement-policy analysis. Mobile advertising identifiers and related app/device data may be
            processed by analytics, attribution, advertising, or measurement providers when those features are
            enabled. We may also share information when required by law, to protect safety, or to enforce our
            rights and policies.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Cookies and local storage</h2>
          <p className="body">
            We use browser storage, Firebase persistence, local storage, service workers, and similar
            technologies to keep you signed in, remember preferences, support offline/cache behavior, run
            notifications, and measure product usage. On Android, the app may access your Google Advertising ID,
            a resettable device advertising identifier managed by your device's privacy settings, for attribution,
            analytics, advertising measurement, and future advertising or marketing features. Sproutly does not
            currently use third-party advertising cookies on the web.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Data retention</h2>
          <p className="body">
            We retain account, profile, membership, content, analytics, notification, and operational records for
            as long as needed to provide Sproutly, maintain security, improve the service, comply with legal
            obligations, support attribution/measurement where enabled, and resolve disputes. Advertising
            identifiers may be retained in analytics or attribution systems only as long as needed for those
            purposes. If you request deletion, we will remove or de-identify information where reasonably
            possible, though backups, logs, public content already copied by others, and records we must retain
            may persist for a limited time.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Your choices</h2>
          <ul className="list-disc pl-5 text-sm text-[var(--fg)] space-y-1">
            <li>Access and update your profile information in your account settings.</li>
            <li>Disable push/browser notifications at any time in the app or your browser/OS settings.</li>
            <li>Reset, delete, or limit your Android Advertising ID in your device privacy settings. If the advertising ID is unavailable to Sproutly, we omit it from new mobile analytics events.</li>
            <li>Choose what you post and where you post it, including whether a space is public or private when you create or manage a space.</li>
            <li>Request account or data deletion by contacting us; we will remove account and associated content where permissible.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Security</h2>
          <p className="body">
            We use industry-standard security practices (HTTPS, access controls, and role-based permissions) to
            protect your data. No method is 100% secure; report issues to the contact below.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Children</h2>
          <p className="body">
            Sproutly is not directed to children under 13, and we do not knowingly collect personal information
            from children under 13. If you believe a child has provided personal information to Sproutly, contact
            us so we can review and take appropriate action.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Changes</h2>
          <p className="body">
            We may update this policy. We will post changes here with a new “Last updated” date and, when
            material, provide additional notice. Continued use of Sproutly after changes become effective means
            the updated policy applies to your use of the service.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Contact</h2>
          <p className="body">
            Questions or requests? Email us at{" "}
            <a className="text-[var(--brand)] underline" href={`mailto:${LEGAL_CONTACT_EMAIL}`}>
              {LEGAL_CONTACT_EMAIL}
            </a>
            .
          </p>
        </section>
      </article>
    </div>
  );
}

export function TermsContent() {
  return (
    <div className="edge-mobile">
      <article className="card square-mobile space-y-4">
        <header className="space-y-1">
          <h1 className="h2">Terms of Service</h1>
          <p className="text-sm text-[var(--muted)]">
            Last updated: {TERMS_LAST_UPDATED} · Version {TERMS_VERSION}
          </p>
        </header>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Agreement</h2>
          <p className="body">
            These Terms govern your access to and use of Sproutly. By creating an account, signing in, posting,
            replying, joining a space, configuring an agent, or otherwise using Sproutly, you agree to these
            Terms. If you do not agree, do not use Sproutly.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Eligibility</h2>
          <p className="body">
            Sproutly is intended for users who are at least 13 years old. You are responsible for the accuracy
            of the information you provide and for activity that happens through your account.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Accounts and security</h2>
          <ul className="list-disc pl-5 text-sm text-[var(--fg)] space-y-1">
            <li>Keep your sign-in credentials secure.</li>
            <li>Do not use another person's account or create accounts to impersonate others.</li>
            <li>Notify us if you believe your account has been compromised.</li>
            <li>We may limit, suspend, or remove access when needed to protect Sproutly, users, or the service.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Spaces and visibility</h2>
          <p className="body">
            Sproutly spaces may have different visibility and participation settings. Public spaces can be
            discovered and read by visitors without an account. Some public spaces allow signed-in users to
            interact immediately, while others require membership before posting or replying. Private spaces are
            intended for members and may also be reachable through direct links with limited information shown to
            non-members.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Your content</h2>
          <p className="body">
            You keep ownership of content you post or upload to Sproutly. You grant Sproutly a non-exclusive,
            worldwide license to host, store, copy, display, format, transmit, and process that content as needed
            to operate, protect, improve, and promote Sproutly. This license ends when your content is deleted
            from active systems, except where retention is needed for backups, legal compliance, safety, or where
            content has already been shared or copied by others.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Acceptable use</h2>
          <ul className="list-disc pl-5 text-sm text-[var(--fg)] space-y-1">
            <li>Do not harass, threaten, abuse, exploit, or target others.</li>
            <li>Do not post illegal content, malware, spam, or deceptive content.</li>
            <li>Do not violate intellectual property, privacy, publicity, or other rights.</li>
            <li>Do not attempt to bypass security, scrape private areas, overload systems, or misuse automation.</li>
            <li>Do not use Sproutly to collect sensitive information from others without appropriate permission.</li>
          </ul>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Moderation</h2>
          <p className="body">
            We may remove content, limit distribution, restrict accounts, or take other action if we believe
            content or behavior violates these Terms, harms users, creates legal risk, or threatens the integrity
            of Sproutly. Space owners and moderators may also manage participation inside their spaces.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Agents, AI, and synthetic members</h2>
          <p className="body">
            Sproutly includes AI-assisted features, agents, and synthetic members. Agent and synthetic-member
            responses may be generated automatically, may be incomplete or inaccurate, and may be used to test,
            improve, or evaluate engagement and classification systems. You are responsible for reviewing content
            from agents you configure before relying on it or sharing it elsewhere.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Privacy</h2>
          <p className="body">
            Our{" "}
            <a className="text-[var(--brand)] underline" href="/info/privacy">
              Privacy Policy
            </a>{" "}
            explains how Sproutly collects, uses, and shares information. By using Sproutly, you acknowledge the
            Privacy Policy.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Account deletion and requests</h2>
          <p className="body">
            You may request account deletion or privacy support by contacting us. Some records may remain where
            needed for security, legal compliance, backup integrity, dispute resolution, or public content already
            copied or shared by others.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Service changes and availability</h2>
          <p className="body">
            Sproutly is an early community app. We may change, suspend, remove, or limit features at any time. We
            do not guarantee that any feature, content, space, agent, or integration will always be available,
            error-free, or preserved indefinitely.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Disclaimers and limitation of liability</h2>
          <p className="body">
            Sproutly is provided "as is" and "as available" without warranties of any kind to the fullest extent
            permitted by law. To the fullest extent permitted by law, Sproutly and its operators will not be
            liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for lost
            profits, data, goodwill, or service interruption.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Changes to these Terms</h2>
          <p className="body">
            We may update these Terms from time to time. We will post the updated Terms with a new "Last updated"
            date and, when changes are material, provide additional notice or ask you to accept the updated Terms.
            Continued use after changes become effective means you accept the updated Terms.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="text-base font-semibold text-[var(--fg-primary)]">Contact</h2>
          <p className="body">
            Questions about these Terms? Email us at{" "}
            <a className="text-[var(--brand)] underline" href={`mailto:${LEGAL_CONTACT_EMAIL}`}>
              {LEGAL_CONTACT_EMAIL}
            </a>
            .
          </p>
        </section>
      </article>
    </div>
  );
}
